import { useCallback, useEffect, useRef, useState } from 'react';
import {
  normalizeFilterModel,
  serializeFilterModel,
  stripQuickFilter,
} from './tableViewFilterUtils';

export default function useGridFilterDraftState({
  initialFilterModel,
  serverSideFiltering,
  setFilterModel,
  updateSettings,
}) {
  const [gridFilterModel, setGridFilterModel] = useState(
    initialFilterModel ?? undefined
  );

  const previousModelSignatureRef = useRef(
    serializeFilterModel(stripQuickFilter(initialFilterModel))
  );
  const previousQuickFilterSignatureRef = useRef(
    serializeFilterModel(initialFilterModel?.quickFilterValues)
  );
  const committedModelSignatureRef = useRef(
    serializeFilterModel(normalizeFilterModel(initialFilterModel))
  );
  const syncedInitialFilterModelSignatureRef = useRef(
    serializeFilterModel(initialFilterModel)
  );
  // The last model this hook pushed out (to the grid settings / parent),
  // without quick filter values.
  const pushedModelSignatureRef = useRef(null);

  useEffect(() => {
    const nextInitialSignature = serializeFilterModel(initialFilterModel);
    if (nextInitialSignature === syncedInitialFilterModelSignatureRef.current) {
      return;
    }

    syncedInitialFilterModelSignatureRef.current = nextInitialSignature;

    // Our own commit coming back in. Committing drops valueless draft items
    // (e.g. a column just picked in the filter panel), so adopting the echo
    // would wipe the draft and snap the panel back to the first column.
    if (
      serializeFilterModel(stripQuickFilter(initialFilterModel)) ===
      pushedModelSignatureRef.current
    ) {
      return;
    }

    previousModelSignatureRef.current = serializeFilterModel(
      stripQuickFilter(initialFilterModel)
    );
    previousQuickFilterSignatureRef.current = serializeFilterModel(
      initialFilterModel?.quickFilterValues
    );
    committedModelSignatureRef.current = serializeFilterModel(
      normalizeFilterModel(initialFilterModel)
    );
    setGridFilterModel(initialFilterModel ?? undefined);
  }, [initialFilterModel]);

  const handleFilterModelChange = useCallback(
    (filterModel) => {
      const { quickFilterValues, ...filterModelWithoutQuickFilter } =
        filterModel || {};

      const nextModelSignature = serializeFilterModel(
        filterModelWithoutQuickFilter
      );
      const nextQuickFilterSignature = serializeFilterModel(quickFilterValues);

      if (
        previousModelSignatureRef.current === nextModelSignature &&
        previousQuickFilterSignatureRef.current === nextQuickFilterSignature
      ) {
        return;
      }

      previousModelSignatureRef.current = nextModelSignature;
      previousQuickFilterSignatureRef.current = nextQuickFilterSignature;
      setGridFilterModel(filterModel ?? undefined);

      if (!serverSideFiltering) {
        pushedModelSignatureRef.current = nextModelSignature;
        updateSettings({ filterModel: filterModelWithoutQuickFilter });
        setFilterModel(filterModel);
        return;
      }

      const committedFilterModel = normalizeFilterModel(filterModel);
      const committedJson = serializeFilterModel(committedFilterModel);

      if (committedJson === committedModelSignatureRef.current) {
        return;
      }

      committedModelSignatureRef.current = committedJson;

      const committedWithoutQuickFilter =
        stripQuickFilter(committedFilterModel);
      pushedModelSignatureRef.current = serializeFilterModel(
        committedWithoutQuickFilter
      );
      updateSettings({ filterModel: committedWithoutQuickFilter });
      setFilterModel(committedFilterModel);
    },
    [serverSideFiltering, setFilterModel, updateSettings]
  );

  return {
    gridFilterModel,
    handleFilterModelChange,
  };
}
